/**
 * Deterministic avatar fallback shared by every surface that renders a person
 * without a profile picture.
 *
 * Two rules, one place: the letter always comes from the first name (falling
 * back to the username), and the tone is derived from a stable identifier so
 * the same person keeps the same color across renders, screens and sessions.
 * Surfaces keep their own size and shape — they only borrow the letter and the
 * `data-avatar-tone` attribute, whose colors live in styles/tokens.css.
 */

/** Tones declared as --color-avatar-tone-N-* in styles/tokens.css. */
export const AVATAR_TONE_COUNT = 8;

export interface AvatarFallbackInput {
  /**
   * Stable identity for the color: profileUserGuid, userId or conversation id.
   * Never a list index or anything that changes between renders.
   */
  seed?: string | number | null;
  /** Display or full name; only its first word is used. */
  name?: string | null;
  /** Used for the letter when there is no name. */
  username?: string | null;
}

export interface AvatarFallback {
  /** Single uppercase letter, or '' when nothing identifies the person. */
  initial: string;
  /** 1-based tone index for the `data-avatar-tone` attribute. */
  tone: number;
}

/**
 * First letter or digit of a name. Leading punctuation and emoji are skipped
 * so a name like "*Sam" still shows an S, and any script is accepted.
 */
const firstLetter = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const match = trimmed.match(/[\p{L}\p{N}]/u);
  return (match?.[0] ?? Array.from(trimmed)[0] ?? '').toUpperCase();
};

export function avatarInitial(name?: string | null, username?: string | null): string {
  return firstLetter(name) || firstLetter(username);
}

export function avatarTone(seed?: string | number | null): number {
  const key = String(seed ?? '').trim().toLowerCase();
  if (!key) return 1;

  // FNV-1a: stable across reloads and spreads sequential ids across the palette,
  // which a plain length or char-sum hash does not.
  let hash = 0x811c9dc5;
  for (let index = 0; index < key.length; index += 1) {
    // eslint-disable-next-line no-bitwise
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (Math.abs(hash) % AVATAR_TONE_COUNT) + 1;
}

export function avatarFallback({ seed, name, username }: AvatarFallbackInput): AvatarFallback {
  return {
    initial: avatarInitial(name, username),
    // Without an id the name still gives a consistent color per person.
    tone: avatarTone(seed ?? name ?? username),
  };
}
